
import { Component, Vue, Prop, Inject } from 'vue-property-decorator';
import { MediaProxy } from '../../types';
import ObservedPromise from '../services/ObservedPromise';
import Media, { getMediaClass } from '../models/Media';
import { CacheStore } from '../services/CacheStore';
import { RecordProxy } from 'spraypaint/lib-esm/proxies';

const _timestamp = +new Date();

@Component({ components: {} })
export default class MediaImg extends Vue {
  @Prop(String) readonly id!: string;

  @Prop(Object) readonly media!: Media;

  @Prop(Boolean) readonly thumbnail!: boolean;

  @Prop(String) readonly rendition!:
    | 'thumbnail'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'original';

  @Prop({ type: String, default: 'div' }) readonly tag!: string;

  @Prop({ type: [Boolean, String] }) readonly gradient!:
    | string
    | true
    | 'bottom'
    | 'top'
    | undefined;

  @Prop({ type: Boolean, default: true }) readonly useCache!: boolean;

  @Prop(Boolean) readonly slotted!: boolean;

  @Prop(String) readonly cacheKey?: string;

  @Inject({ default: null }) readonly cacheStore!: CacheStore | null;

  now: number | null = null;

  get theCacheKey() {
    return this.cacheKey || getMediaClass().cacheName || 'media';
  }

  get cache() {
    if (!this.useCache) {
      return undefined;
    }
    const store = this.cacheStore || CacheStore.default;
    return store.get<Promise<MediaProxy>>(this.theCacheKey);
  }

  get mediaPromise(): ObservedPromise<MediaProxy> {
    if (this.media) {
      const { media } = this;
      const promise = new Promise<RecordProxy<Media>>(resolve => {
        const proxy = new RecordProxy(this.media.asPersisted(), {
          get data() {
            return media.asJSON().data;
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
        resolve(proxy);
      });
      return new ObservedPromise(promise);
    }
    if (this.id && this.now !== 0) {
      let cached = this.cache?.get(this.id);
      if (!cached) {
        const Klass = getMediaClass();
        cached = Klass.find(this.id);
        if (this.cache) {
          this.cache.put(this.id, cached);
        }
      }
      return new ObservedPromise(cached);
    }
    return new ObservedPromise();
  }

  get src() {
    const { renditions } = this.mediaPromise.value?.data || {};
    const { thumbnail, original } = renditions || {};
    if (this.rendition && renditions) {
      const found = renditions[this.rendition];
      if (found) {
        return found;
      }
    }
    return (this.thumbnail && thumbnail) || original || null;
  }

  get gradientValue() {
    let { gradient } = this;
    if (gradient === true || gradient === 'bottom') {
      gradient = '0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0)';
    } else if (gradient === 'top') {
      gradient = '180deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0)';
    }
    return gradient;
  }

  get vImgAttrs() {
    const { gradientValue } = this;
    return { ...this.$attrs, gradient: gradientValue || undefined };
  }

  get errorStyle() {
    const backgroundImage: string[] = [];
    if (this.gradientValue) {
      backgroundImage.push(`linear-gradient(${this.gradientValue})`);
    }
    return {
      backgroundImage: backgroundImage.join(', '),
    };
  }

  // Handle a one-off reload of the media if the Expires timestamp is before now
  handleError(src: string) {
    if (this.now || !src || typeof src !== 'string') {
      return;
    }
    const pattern = /\bExpires=(\d+)/;
    const matches = src.match(pattern);
    if (!matches) {
      return;
    }
    const timestamp = parseInt(matches[1], 10);
    if (!timestamp || timestamp < _timestamp) {
      return;
    }
    const now = +new Date();
    if (now && timestamp < now) {
      if (this.id && this.cache) {
        this.cache.delete(this.id);
      }
      // Trigger a mediaPromise recompute
      this.now = now;
    }
  }
}
